import { useState, useId, type ReactElement, type ReactNode } from 'react';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { cn } from '../../lib/cn';
import { DUR, EASE } from '../../interaction';

export type TooltipPlacement = 'top' | 'bottom' | 'start' | 'end';

/** `start`/`end` are logical, so the tip stays on the reading-start edge in RTL. */
const SIDES: Record<TooltipPlacement, 'top' | 'bottom' | 'inline-start' | 'inline-end'> = {
  top: 'top',
  bottom: 'bottom',
  start: 'inline-start',
  end: 'inline-end',
};

export interface TooltipProps {
  content: ReactNode;
  /** The element the tip describes. It is cloned, so it must accept a ref. */
  trigger: ReactElement;
  placement?: TooltipPlacement;
  /** Hover rest delay before the tip opens. Focus opens on the same delay. */
  openDelay?: number;
  disabled?: boolean;
  className?: string;
}

/**
 * Hover/focus tip for a control whose label is not visible.
 *
 * **A tooltip is decoration.** It never carries the accessible name: an
 * icon-only control states its own name with `aria-label` (see `IconButton`),
 * because the tip is only in the tree while it is open and is not announced at
 * all on a touch device. This component wires the tip as `role="tooltip"` and
 * points the trigger at it with `aria-describedby` — a description, never a
 * name.
 *
 * Both entry paths are Base UI's: hover, and focus *only when the focus ring is
 * visible* — `useFocus` returns early unless `matchesFocusVisible(target)`
 * (`floating-ui-react/hooks/useFocus.js`), so clicking a button with the mouse
 * does not pop a tip. Placement flips on collision (`collisionAvoidance`).
 */
export function Tooltip({
  content,
  trigger,
  placement = 'top',
  openDelay = 400,
  disabled = false,
  className,
}: TooltipProps) {
  const reduceMotion = useReducedMotion();
  const popupId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const side = SIDES[placement];

  return (
    <BaseTooltip.Root open={isOpen} onOpenChange={setIsOpen} disabled={disabled}>
      <BaseTooltip.Trigger
        delay={openDelay}
        render={trigger}
        aria-describedby={isOpen ? popupId : undefined}
      />
      <BaseTooltip.Portal keepMounted>
        <AnimatePresence>
          {isOpen ? (
            /* `hidden={false}`: Base UI hides the positioner the moment the
               store closes (`tooltip/positioner/TooltipPositioner.js`); this
               component keeps it in the DOM for the exit animation. */
            <BaseTooltip.Positioner
              key="tooltip"
              hidden={false}
              side={side}
              align="center"
              sideOffset={8}
              collisionAvoidance={{ side: 'flip', align: 'shift' }}
              // `--z-tooltip` (700) sits above the menu/popover layer, so a tip
              // raised from a row inside a menu or a table popover is drawn over
              // the surface that owns it rather than inside it.
              className="z-[var(--z-tooltip)]"
            >
              <BaseTooltip.Popup
                id={popupId}
                role="tooltip"
                className={cn(
                  // `shadow-hard`, not `shadow-md`: the tip is ink on an inverse
                  // surface and the soft blur is the one elevation language this
                  // system replaced — it is also the one that vanishes in dark.
                  'max-w-64 rounded-xs border border-line-strong bg-surface-inverse px-2.5 py-1.5',
                  'font-body text-xs text-inverse shadow-hard',
                  className,
                )}
                render={
                  <motion.div
                    initial={reduceMotion ? false : { opacity: 0, y: side === 'top' ? 4 : -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 0 }}
                    transition={reduceMotion ? { duration: 0 } : { duration: DUR.fast, ease: EASE.out }}
                  />
                }
              >
                {content}
              </BaseTooltip.Popup>
            </BaseTooltip.Positioner>
          ) : null}
        </AnimatePresence>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}
